import { useCallback, useEffect, useState } from 'react';
import { Pressable, Switch } from 'react-native';
import { useRouter } from 'expo-router';
import { Text, XStack, YStack } from 'tamagui';
import { useThemeColors } from '@kiips/ui';
import { AdaptivePanel } from '@kiips/ui';
import { useConsentStore } from '../../store/consent-store';
import {
  ACCEPT_ALL_DECISION,
  INITIAL_TOGGLE_DEFAULTS,
  REJECT_ALL_DECISION,
  buildDraftFromToggles,
  type ConsentDraft,
} from './consent-sheet-contract';

/**
 * Single-surface tracking-consent sheet — see ADR-0065.
 *
 * One screen carries everything: header copy, the two per-purpose
 * toggle rows (all defaulting OFF), a "Save preferences" inline link
 * under the toggles, a privacy-policy link, and a footer with two
 * equally-weighted buttons. The footer is the binary global choice
 * (Accept all / Reject all) — both always one-tap, both share the
 * same outlined treatment. The inline Save link commits whatever the
 * toggles are currently set to, for users who want a granular
 * selection. This structure makes withdrawal as easy as giving
 * (GDPR Art. 7(3) / EDPB Guidelines 03/2022) regardless of which
 * entry path the user came through.
 *
 * Mounted once at the root of the app tree (see `_layout.tsx`). The
 * `/profile` "Privacy & Tracking" entry calls `openReview()` on the
 * consent store to re-open the sheet with the current decision
 * pre-loaded into the toggles.
 */
export function ConsentSheet() {
  const router = useRouter();
  const consent = useConsentStore((s) => s.consent);
  const hasHydrated = useConsentStore((s) => s.hasHydrated);
  const reviewOpen = useConsentStore((s) => s.reviewOpen);
  const dismissedThisSession = useConsentStore((s) => s.dismissedThisSession);
  const setConsent = useConsentStore((s) => s.setConsent);
  const closeReview = useConsentStore((s) => s.closeReview);
  const dismissFirstPrompt = useConsentStore((s) => s.dismissFirstPrompt);
  const needsPrompt = useConsentStore((s) => s.needsPrompt());

  const [draft, setDraft] = useState<ConsentDraft>(INITIAL_TOGGLE_DEFAULTS);

  // First-launch dismiss is allowed per ADR-0065 — suppress the prompt
  // for the rest of this session so it doesn't re-snap open. Review
  // sessions go through `reviewOpen` and are unaffected.
  const open =
    hasHydrated && ((needsPrompt && !dismissedThisSession) || reviewOpen);

  // Two open-paths drive different initial state:
  //   - First-launch (needsPrompt, !reviewOpen): toggles at OFF,
  //     matching the opt-in posture. Save-preferences from this state
  //     yields all-off, identical to the Reject-all footer button.
  //   - Review (reviewOpen): pre-load the current decision so the
  //     user sees what they previously chose. Reject-all in the footer
  //     ignores this seed and commits all-off regardless.
  //
  // `consent` is intentionally OMITTED from the dep list: re-running
  // on a server-side hydrate would clobber the user's in-flight draft.
  // We only seed the draft when the sheet (re-)opens — current consent
  // is read via closure at that point.
  useEffect(() => {
    if (!open) return;
    if (reviewOpen && consent) {
      setDraft({
        analytics: consent.analytics,
        affiliates: consent.affiliates,
      });
    } else {
      setDraft(INITIAL_TOGGLE_DEFAULTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reviewOpen]);

  const handleAcceptAll = useCallback(() => {
    setConsent(ACCEPT_ALL_DECISION);
    closeReview();
  }, [setConsent, closeReview]);

  const handleRejectAll = useCallback(() => {
    setConsent(REJECT_ALL_DECISION);
    closeReview();
  }, [setConsent, closeReview]);

  const handleSavePreferences = useCallback(() => {
    setConsent(buildDraftFromToggles(draft));
    closeReview();
  }, [draft, setConsent, closeReview]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) return;
      // Dismissal without decision (drag-down on phone, backdrop tap
      // on desktop). For first-launch, suppress this session so the
      // sheet stays closed; consent stays null and re-prompts next
      // session per ADR-0065. For review, just close the review flag.
      if (reviewOpen) {
        closeReview();
      } else {
        dismissFirstPrompt();
      }
    },
    [reviewOpen, closeReview, dismissFirstPrompt],
  );

  const handlePrivacyLink = useCallback(() => {
    router.push('/privacy');
  }, [router]);

  // Hold rendering until AsyncStorage rehydrate completes — a flash of
  // the sheet for a user who has already decided would be a jarring
  // regression. After that, leave AdaptivePanel mounted so it can run
  // its own exit animation when `open` flips false.
  if (!hasHydrated) return null;

  return (
    <AdaptivePanel
      open={open}
      onOpenChange={handleOpenChange}
      mode="modal"
      // The sheet sizes to its content — the consent surface is short
      // enough that the old percent-snap layout left it scrolling
      // separately inside a fixed-height frame. Fit mode makes the
      // whole drawer one continuous surface.
      snapPointsMode="fit"
      header={
        <YStack gap={5}>
          <Text fontSize={18} fontWeight="800" color="$textOnSurface">
            How Kiips uses your data
          </Text>
          <Text
            fontSize={11}
            fontWeight="700"
            color="$textSubdued"
            letterSpacing={0.4}
            textTransform="uppercase"
          >
            Off by default · change anytime
          </Text>
        </YStack>
      }
      footer={
        <XStack gap={10} paddingTop={12}>
          <EqualWeightButton
            label="Accept all"
            onPress={handleAcceptAll}
            accessibilityLabel="Accept all tracking purposes"
          />
          <EqualWeightButton
            label="Reject all"
            onPress={handleRejectAll}
            accessibilityLabel="Reject all tracking purposes"
          />
        </XStack>
      }
    >
      <ConsentBody
        draft={draft}
        onChange={setDraft}
        onSavePreferences={handleSavePreferences}
        onPrivacyLink={handlePrivacyLink}
      />
    </AdaptivePanel>
  );
}

// ---------- Body ----------

function ConsentBody({
  draft,
  onChange,
  onSavePreferences,
  onPrivacyLink,
}: {
  draft: ConsentDraft;
  onChange: (next: ConsentDraft) => void;
  onSavePreferences: () => void;
  onPrivacyLink: () => void;
}) {
  return (
    <YStack>
      <ToggleRow
        title="Analytics & diagnostics"
        description="Anonymous usage stats and crash reports (PostHog) to spot broken flows. No trip data."
        value={draft.analytics}
        onChange={(v) => onChange({ ...draft, analytics: v })}
      />
      <ToggleRow
        title="Affiliate deals"
        description="Booking partner links. We earn a commission; partners may set cookies."
        value={draft.affiliates}
        onChange={(v) => onChange({ ...draft, affiliates: v })}
        isLast
      />
      <XStack
        alignItems="center"
        justifyContent="space-between"
        paddingTop={12}
        paddingBottom={2}
      >
        <Pressable
          onPress={onPrivacyLink}
          accessibilityRole="link"
          accessibilityLabel="Read the full privacy policy"
          hitSlop={10}
        >
          <Text
            fontSize={12}
            color="$textSubdued"
            textDecorationLine="underline"
          >
            Privacy policy
          </Text>
        </Pressable>
        <Pressable
          onPress={onSavePreferences}
          accessibilityRole="button"
          accessibilityLabel="Save tracking preferences"
          hitSlop={10}
        >
          <Text
            fontSize={13}
            fontWeight="700"
            color="$accentCeruleanInk"
            textDecorationLine="underline"
          >
            Save preferences
          </Text>
        </Pressable>
      </XStack>
    </YStack>
  );
}

function ToggleRow({
  title,
  description,
  value,
  onChange,
  isLast = false,
}: {
  title: string;
  description: string;
  value: boolean;
  onChange: (next: boolean) => void;
  isLast?: boolean;
}) {
  const theme = useThemeColors();
  return (
    <XStack
      paddingVertical={10}
      borderBottomWidth={isLast ? 0 : 1}
      borderBottomColor="$dividerSubtle"
      alignItems="center"
      gap={12}
    >
      <YStack flex={1} gap={1} minWidth={0}>
        <XStack alignItems="center" gap={6}>
          <Text fontSize={13} fontWeight="700" color="$textOnSurface">
            {title}
          </Text>
          {/* Cerulean dot mirrors the switch state inline, so the active
              purposes read at a glance without parsing each toggle. */}
          {value ? (
            <YStack
              width={6}
              height={6}
              borderRadius={3}
              backgroundColor={theme.accentCerulean}
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
            />
          ) : null}
        </XStack>
        <Text fontSize={11} color="$textSubdued" lineHeight={15}>
          {description}
        </Text>
      </YStack>
      <Switch
        value={value}
        onValueChange={onChange}
        trackColor={{ false: theme.borderLight, true: theme.textOnSurface }}
        thumbColor={theme.surface}
        accessibilityLabel={`${title} consent toggle`}
      />
    </XStack>
  );
}

// ---------- Shared button primitive ----------

/**
 * The single button shape used by the footer. Both footer buttons
 * render through this one primitive with identical surface, border,
 * ink, geometry and typography — there is no primary/secondary
 * distinction. Accept-all and Reject-all read as equally-weighted
 * options, which is the chromatic side of the GDPR equivalence
 * argument: a lower-weight reject button is exactly the dark pattern
 * EDPB Guidelines 03/2022 forbid. The inline "Save preferences" link
 * is rendered separately inside ConsentBody and intentionally does
 * NOT use this primitive — it's visually subordinate to the binary
 * footer choices.
 */
function EqualWeightButton({
  label,
  onPress,
  accessibilityLabel,
}: {
  label: string;
  onPress: () => void;
  accessibilityLabel: string;
}) {
  const theme = useThemeColors();
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={({ pressed }) => [
        {
          flex: 1,
          height: 48,
          borderRadius: 24,
          backgroundColor: theme.surfaceCanvas,
          borderWidth: 1.5,
          borderColor: theme.borderDefault,
          alignItems: 'center',
          justifyContent: 'center',
          paddingHorizontal: 16,
        },
        // Faint fill + border darken on press, applied through the one
        // shared primitive so both footer buttons stay equal-weight.
        pressed && {
          transform: [{ scale: 0.98 }],
          backgroundColor: theme.surfacePressed,
          borderColor: theme.textSubdued,
        },
      ]}
    >
      <Text fontSize={14} fontWeight="700" color={theme.textOnSurface}>
        {label}
      </Text>
    </Pressable>
  );
}
